import styles from './OrderStatGrid.module.css';

export interface OrderStat {
    label: string;
    /** A count, or preformatted text such as "12/100" */
    value: number | string;
    /** Whether the stat counts as 0 for the all-zero (grey) look. Defaults to `value === 0`, so set it for text values. */
    isZero?: boolean;
}

interface OrderStatGridProps {
    stats: OrderStat[];
    isLoading?: boolean;
    /** Smaller tiles, for when something else (e.g. a banner) shares the fixed-height card with the grid */
    compact?: boolean;
}

type TileLayout = 'default' | 'compact' | 'inline';

// Up to 3 stats sit in one row of fixed-height tiles, label above value — or, when `compact`, shorter
// tiles sized to their content with a smaller value. 4 switch to a 2x2 grid whose rows share the
// remaining height instead, with each tile's label and value inline — so the grid still fits the
// fixed-height card.
const isTwoByTwo = (count: number) => count === 4;

const TILE_LAYOUT_CLASS_NAMES: Record<TileLayout, string> = {
    default: `flex-col justify-between p-4 ${styles.fixedHeight}`,
    compact: 'flex-col gap-1 p-3',
    inline: 'h-full items-center justify-between p-4',
};

const OrderStatTile: React.FC<OrderStat & { isLoading?: boolean; layout: TileLayout; isEmpty: boolean }> = ({
    label,
    value,
    isLoading,
    layout,
    isEmpty,
}) => (
    <div
        className={`flex gap-2 rounded-xl ${TILE_LAYOUT_CLASS_NAMES[layout]} ${isEmpty ? styles.tileEmpty : styles.tile}`}
    >
        <div className="text-body-md font-semibold">{label}</div>
        <div className={`font-bold ${layout === 'default' ? 'text-heading-xl' : 'text-heading-lg'}`}>
            {isLoading ? '–' : value}
        </div>
    </div>
);

export const OrderStatGrid: React.FC<OrderStatGridProps> = ({ stats, isLoading, compact }) => {
    const twoByTwo = isTwoByTwo(stats.length);
    const layout: TileLayout = twoByTwo ? 'inline' : compact ? 'compact' : 'default';
    // Also true while loading (every value is still 0), so the tiles start out neutral
    const isEmpty = stats.every((stat) => stat.isZero ?? stat.value === 0);

    return (
        <div
            className={
                twoByTwo ? 'grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-3' : 'grid grid-cols-1 gap-3 sm:grid-cols-3'
            }
        >
            {stats.map(({ label, value }) => (
                <OrderStatTile
                    key={label}
                    label={label}
                    value={value}
                    isLoading={isLoading}
                    layout={layout}
                    isEmpty={isEmpty}
                />
            ))}
        </div>
    );
};
